import React, { useState } from "react";
import { Form, DatePicker, Collapse } from "antd";
import ItineraryForm from "../../pages/EditForm/ItineraryForm";
import locale from "antd/es/date-picker/locale/zh_TW";
import dayjs from "dayjs";

dayjs.locale('zh-tw');

const { Panel } = Collapse;

interface ItinerarySectionProps {
    form: any;
    initialValues?: any;
    tiaData?: any;
    tourInformationForm?: any;
    firstDay?: any;
    lastDay?: any;
}

const config = {
    rules: [
        {
            required: true,
            message: "此項為必填。",
        },
    ],
};

export default function ItinerarySection({ 
    form, 
    initialValues, 
    tiaData, 
    tourInformationForm,
    firstDay: externalFirstDay,
    lastDay: externalLastDay 
}: ItinerarySectionProps) {
    const [itineraryDetails, setItineraryDetails] = useState<any>({});
    const [otherFacility, setOtherFacility] = useState(false);
    const [firstDay, setFirstDay] = useState<any>(externalFirstDay || null);
    const [lastDay, setLastDay] = useState<any>(externalLastDay || null);

    const facilityTypeList = tiaData?.typeOfFacilityList || ["景點", "餐饍", "住宿", "註冊商店"];
    const facilityList = tiaData?.facilityList || [];
    
    // Convert facility list to Map
    const facilityData = new Map();
    if (Array.isArray(facilityList)) {
        facilityList.forEach((item: any) => {
            if (item.facilityType && item.facility) {
                facilityData.set(item.facilityType, item.facility);
            }
        });
    }

    // Initialize itineraryDetails and dates when form loads with existing data
    React.useEffect(() => {
        // Get current form values
        const formValues = form.getFieldsValue();
        
        // Initialize dates from form if available
        if (formValues.arrivalDate && dayjs.isDayjs(formValues.arrivalDate)) {
            if (!firstDay || !firstDay.isSame(formValues.arrivalDate, 'day')) {
                setFirstDay(formValues.arrivalDate);
            }
        }
        if (formValues.depatureDate && dayjs.isDayjs(formValues.depatureDate)) {
            if (!lastDay || !lastDay.isSame(formValues.depatureDate, 'day')) {
                setLastDay(formValues.depatureDate);
            }
        }
        
        // Initialize itineraryDetails from itineraryData
        if (formValues.itineraryData && Array.isArray(formValues.itineraryData)) {
            const details: any = {};
            formValues.itineraryData.forEach((day: any, dayIndex: number) => {
                if (day?.itinerary) {
                    details[dayIndex] = {};
                    Object.keys(day.itinerary).forEach((key) => {
                        const index = parseInt(key.replace('itinerary', ''));
                        const itineraryItem = day.itinerary[key];
                        if (itineraryItem?.facilityType) {
                            details[dayIndex][index] = itineraryItem.facilityType;
                        }
                    });
                }
            });
            if (Object.keys(details).length > 0 && JSON.stringify(details) !== JSON.stringify(itineraryDetails)) {
                setItineraryDetails(details);
            }
        }
    }, [form, initialValues]);

    return (
        <Collapse defaultActiveKey={["1"]}>
            <Panel 
                header={
                    <h3>
                        第 IV 部分：內地入境旅行團行程
                        <span style={{ backgroundColor: "yellow" }}>
                            （須按照入境旅行團行程安排，就每項行程逐一添加行程資料）
                        </span>
                    </h3>
                } 
                key="1" 
                forceRender
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                >
                    <Form.Item
                        name="arrivalDate"
                        label="抵港日期"
                        rules={[{ required: true, message: "請選擇抵港日期" }]}
                    >
                        <DatePicker
                            locale={locale}
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            placeholder="請選擇日期"
                            onChange={(date) => {
                                setFirstDay(date);
                                // Initialize itineraryDetails with first day when date is set
                                if (date && !itineraryDetails[0]) {
                                    setItineraryDetails({ ...itineraryDetails, 0: { 0: null } });
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="depatureDate"
                        label="離港日期"
                        rules={[{ required: true, message: "請選擇離港日期" }]}
                    >
                        <DatePicker
                            locale={locale}
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            placeholder="請選擇日期"
                            onChange={(date) => {
                                setLastDay(date);
                            }}
                        />
                    </Form.Item>
                    <ItineraryForm
                        form={form}
                        config={config}
                        tourInformationForm={tourInformationForm || form}
                        facilityData={facilityData}
                        itineraryDetails={itineraryDetails}
                        setItineraryDetails={setItineraryDetails}
                        firstDay={externalFirstDay || firstDay}
                        lastDay={externalLastDay || lastDay}
                        facilityTypeList={facilityTypeList}
                        otherFacility={otherFacility}
                        setOtherFacility={setOtherFacility}
                    />
                </Form>
            </Panel>
        </Collapse>
    );
}
